import { getTestBySlug } from '@/features/tests/data/tests';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapTestRunDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { buildEqFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  TestRunDto,
  TestRunStatusSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const testRunSortFieldMap: Readonly<Record<string, string>> = {
  runNumber: 'timestamp',
};

const mapTestRunSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(testRunSortFieldMap, field)
      ? testRunSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export const queryTestRuns = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    search?: string;
  },
): Promise<QueryResult<TestRunDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts
    ? mapTestRunSorts(input.sorts)
    : [{ field: 'timestamp', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<TestRunDto>>(
      'test-runs/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        search: input?.search,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapTestRunDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestRunStatusSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
): Promise<TestRunStatusSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapTestRunSorts(input.sorts) : [];
  try {
    return await apiClient.post<TestRunStatusSummaryDto>(
      'test-runs/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getTestRunById = async (
  id: string,
  organisationId?: string | null,
): Promise<TestRunDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<TestRunDto>(`test-runs/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapTestRunDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getTestRunByRunNumber = async (
  testSlug: string | null,
  runNumber: number | null,
  projectId?: string | null,
  organisationId?: string | null,
): Promise<TestRunDto | null> => {
  if (!testSlug || runNumber === null || !Number.isFinite(runNumber) || runNumber < 1) {
    return null;
  }
  let test = await getTestBySlug(testSlug, projectId, organisationId);
  if (!test && projectId) {
    test = await getTestBySlug(testSlug, null, organisationId);
  }
  if (!test) {
    return null;
  }
  const resolvedProjectId = test.projectId;
  const filters = buildEqFilter('testId', test.id);
  const pageSize = 100;
  const runSorts = [
    { field: 'timestamp', direction: 'asc' },
    { field: 'id', direction: 'asc' },
  ] satisfies QuerySort[];
  const firstPage = await queryTestRuns(resolvedProjectId, organisationId, {
    page: 1,
    pageSize,
    sorts: runSorts,
    filters,
  });
  const firstMatch = firstPage.items.find((item) => item.runNumber === runNumber);
  if (firstMatch) return firstMatch;
  const maxPage = Math.max(1, Math.ceil(firstPage.total / pageSize));
  if (maxPage === 1) return null;
  const targetPage = Math.min(maxPage, Math.max(2, Math.ceil(runNumber / pageSize)));
  if (targetPage !== 1) {
    const targetResult = await queryTestRuns(resolvedProjectId, organisationId, {
      page: targetPage,
      pageSize,
      sorts: runSorts,
      filters,
    });
    const targetMatch = targetResult.items.find((item) => item.runNumber === runNumber);
    if (targetMatch) return targetMatch;
  }
  for (let page = 2; page <= maxPage; page += 1) {
    if (page === targetPage) continue;
    const result = await queryTestRuns(resolvedProjectId, organisationId, {
      page,
      pageSize,
      sorts: runSorts,
      filters,
    });
    const match = result.items.find((item) => item.runNumber === runNumber);
    if (match) return match;
    if (result.items.length < pageSize) break;
  }
  return null;
};

export const createTestRun = async (
  input: {
    suiteRunId: string;
    testId: string;
    status?: TestRunDto['status'];
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    failureMessage?: string | null;
    failureType?: string | null;
    failureStack?: string | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
): Promise<TestRunDto> => {
  const token = ensureToken();
  const body = {
    suiteRunId: input.suiteRunId.trim(),
    testId: input.testId.trim(),
    status: input.status,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    failureMessage: input.failureMessage ?? undefined,
    failureType: input.failureType ?? undefined,
    failureStack: input.failureStack ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.post<TestRunDto>('test-runs', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestRunDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateTestRun = async (
  input: {
    id: string;
    tags?: string[];
    status?: TestRunDto['status'];
    timestamp?: Date | string | null;
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    failureMessage?: string | null;
    failureType?: string | null;
    failureStack?: string | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
): Promise<TestRunDto> => {
  const token = ensureToken();
  const body = {
    tags: input.tags,
    status: input.status,
    timestamp: input.timestamp ?? undefined,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    failureMessage: input.failureMessage ?? undefined,
    failureType: input.failureType ?? undefined,
    failureStack: input.failureStack ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.patch<TestRunDto>(`test-runs/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestRunDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteTestRun = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`test-runs/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restoreTestRun = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`test-runs/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
